import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { SUPPORTED_LOCALES, LOCALE_TO_HTML_LANG } from "@/i18n/translations";
import { blogArticles } from "@/data/blog-articles";

const BASE_URL = "https://fuegosjlnordic.com";

const LOCALE_TO_OG: Record<string, string> = {
  no: "nb_NO",
  en: "en_GB",
  se: "sv_SE",
  dk: "da_DK",
};

const LOCALE_URL_PREFIX: Record<string, string> = {
  no: "",
  en: "/en",
  se: "/se",
  dk: "/dk",
};

/** Map pathname to a route-level SEO key prefix */
const getPageKey = (pathname: string): string => {
  const clean = pathname.replace(/^\/(en|se|dk)\/?/, "/").replace(/\/$/, "") || "/";
  if (clean === "/") return "home";
  if (clean === "/tilbehor") return "accessories";
  if (clean === "/kontakt") return "contact";
  if (clean === "/personvern") return "privacy";
  if (clean === "/vilkar") return "terms";
  if (clean === "/blogg") return "blog";
  if (clean.startsWith("/blogg/")) return "blogpost";
  return "home";
};

const BREADCRUMB_MAP: Record<string, { nameKey: string }> = {
  home: { nameKey: "nav.home" },
  accessories: { nameKey: "nav.accessories" },
  contact: { nameKey: "footer.contact" },
  privacy: { nameKey: "footer.privacy" },
  terms: { nameKey: "footer.terms" },
  blog: { nameKey: "nav.blog" },
};

const SEOHead = () => {
  const { locale, t } = useLanguage();
  const { pathname } = useLocation();

  useEffect(() => {
    const pageKey = getPageKey(pathname);
    const cleanPath = pathname.replace(/^\/(en|se|dk)\/?/, "/").replace(/\/$/, "") || "/";

    // Resolve blog post article if applicable
    const blogSlug = cleanPath.startsWith("/blogg/") ? cleanPath.replace("/blogg/", "") : null;
    const blogArticle = blogSlug ? blogArticles.find((a) => a.slug === blogSlug) : null;

    // --- Dynamic <title> & meta description ---
    if (pageKey === "blogpost" && blogArticle) {
      document.title = t(blogArticle.metaTitleKey);
    } else {
      document.title = t(`seo.${pageKey}.title`);
    }

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const setMetaProp = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const description = pageKey === "blogpost" && blogArticle ? t(blogArticle.metaDescKey) : t(`seo.${pageKey}.desc`);
    setMeta("description", description);

    // --- Canonical ---
    const routePath = pathname.replace(/^\/(en|se|dk)\/?/, "/").replace(/\/$/, "");
    const finalCanonical = locale === "no" && routePath === "" ? `${BASE_URL}/` : `${BASE_URL}${LOCALE_URL_PREFIX[locale]}${routePath}/`;

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.rel = "canonical";
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = finalCanonical;

    // --- OG tags ---
    const title = pageKey === "blogpost" && blogArticle ? t(blogArticle.metaTitleKey) : t(`seo.${pageKey}.title`);
    setMetaProp("og:title", title);
    setMetaProp("og:description", description);
    setMetaProp("og:url", finalCanonical);
    setMetaProp("og:locale", LOCALE_TO_OG[locale]);
    setMetaProp("og:type", "website");
    setMetaProp("og:site_name", "Fuegos JL Nordic");
    setMetaProp("og:image", `${BASE_URL}/og-image.jpg`);
    setMetaProp("og:image:width", "1200");
    setMetaProp("og:image:height", "630");

    // og:locale:alternate
    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach(el => el.remove());
    SUPPORTED_LOCALES.filter(l => l !== locale).forEach(l => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:locale:alternate");
      meta.content = LOCALE_TO_OG[l];
      document.head.appendChild(meta);
    });

    // Twitter
    setMetaProp("twitter:card", "summary_large_image");
    setMetaProp("twitter:title", title);
    setMetaProp("twitter:description", description);
    setMetaProp("twitter:image", `${BASE_URL}/og-image.jpg`);

    // --- hreflang tags ---
    document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());

    SUPPORTED_LOCALES.forEach(loc => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = LOCALE_TO_HTML_LANG[loc];
      const locPath = loc === "no" ? routePath : `/${loc}${routePath}`;
      link.href = `${BASE_URL}${locPath}/`;
      link.setAttribute("data-hreflang", "true");
      document.head.appendChild(link);
    });

    const xDefault = document.createElement("link");
    xDefault.rel = "alternate";
    xDefault.hreflang = "x-default";
    xDefault.href = `${BASE_URL}${routePath}/`;
    xDefault.setAttribute("data-hreflang", "true");
    document.head.appendChild(xDefault);

    // --- JSON-LD: Product List ---
    const existingScript = document.getElementById("product-jsonld");
    if (existingScript) existingScript.remove();

    const products = [
      { name: "Fuegos JL Ivar (Cowboy)", descKey: "prod.ivar.desc" },
      { name: "Fuegos JL Ivar God", descKey: "prod.ivar_god.desc" },
      { name: "Fuegos JL Rollo", descKey: "prod.rollo.desc" },
      { name: "Fuegos JL Björn 80", descKey: "prod.bjorn80.desc" },
      { name: "Fuegos JL Björn 100", descKey: "prod.bjorn100.desc" },
      { name: "Fuegos JL Björn 120", descKey: "prod.bjorn120.desc" },
      { name: "Fuegos JL Ram 120", descKey: "prod.ram.desc" },
      { name: "Fuegos JL Ragnar 80", descKey: "prod.ragnar80.desc" },
      { name: "Fuegos JL Ragnar 100", descKey: "prod.ragnar100.desc" },
      { name: "Fuegos JL Ragnar 120", descKey: "prod.ragnar120.desc" },
      { name: "Fuegos JL Ragnar Pro", descKey: "prod.ragnar_pro.desc" },
      { name: "Fuegos JL Odin", descKey: "prod.odin.desc" },
      { name: "Fuegos JL Odin Pro", descKey: "prod.odin_pro.desc" },
      { name: "Fuegos JL Floki", descKey: "prod.floki.desc" },
      { name: "Fuegos JL Floki Pro", descKey: "prod.floki_pro.desc" },
      { name: "Fuegos JL Floki (Rodeo)", descKey: "prod.floki.desc" },
      { name: "Fuegos JL Floki Pro (Rodeo Pro)", descKey: "prod.floki_pro.desc" },
      { name: "Fuegos JL Siggy (Pizzaovn)", descKey: "prod.siggy.desc" },
      { name: "Fuegos JL Ubbe (Smoker)", descKey: "prod.ubbe.desc" },
      { name: "Fuegos JL Qatar 2022", descKey: "prod.qatar.desc" },
      { name: "Fuegos JL Stoker (Fogón)", descKey: "prod.stoker.desc" },
    ];

    const productJsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Fuegos JL Nordic Griller",
      "description": t("seo.description"),
      "url": `${BASE_URL}/#produkter`,
      "numberOfItems": products.length,
      "itemListElement": products.map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "Product",
          "name": p.name,
          "description": t(p.descKey),
          "brand": { "@type": "Brand", "name": "Fuegos JL" },
          "category": "Griller",
          "material": "Stål",
          "countryOfOrigin": { "@type": "Country", "name": "Argentina" },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "12",
            "bestRating": "5",
            "worstRating": "1",
          },
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/PreOrder",
            "priceCurrency": "NOK",
            "price": "0",
            "priceValidUntil": "2026-12-31",
            "areaServed": ["NO", "SE", "DK"],
          },
        },
      })),
    };

    const script = document.createElement("script");
    script.id = "product-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(productJsonLd);
    document.head.appendChild(script);

    // --- JSON-LD: FAQ (homepage only) ---
    const existingFaqScript = document.getElementById("faq-jsonld");
    if (existingFaqScript) existingFaqScript.remove();

    if (pageKey === "home") {
      const faqs = [
        { q: "faq.q1", a: "faq.a1" },
        { q: "faq.q2", a: "faq.a2" },
        { q: "faq.q3", a: "faq.a3" },
        { q: "faq.q4", a: "faq.a4" },
        { q: "faq.q5", a: "faq.a5" },
        { q: "faq.q6", a: "faq.a6" },
        { q: "faq.q7", a: "faq.a7" },
        { q: "faq.q8", a: "faq.a8" },
      ];
      const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": t(f.q),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": t(f.a),
          },
        })),
      };
      const faqScript = document.createElement("script");
      faqScript.id = "faq-jsonld";
      faqScript.type = "application/ld+json";
      faqScript.textContent = JSON.stringify(faqJsonLd);
      document.head.appendChild(faqScript);
    }

    // --- JSON-LD: BreadcrumbList ---
    const existingBreadcrumb = document.getElementById("breadcrumb-jsonld");
    if (existingBreadcrumb) existingBreadcrumb.remove();

    const breadcrumbItems: { name: string; url: string }[] = [
      { name: t("nav.home") || "Hjem", url: `${BASE_URL}/` },
    ];

    if (pageKey !== "home" && BREADCRUMB_MAP[pageKey]) {
      breadcrumbItems.push({
        name: t(BREADCRUMB_MAP[pageKey].nameKey),
        url: finalCanonical,
      });
    }

    // Blog post breadcrumb: Hjem > Blogg > Artikkeltittel
    if (pageKey === "blogpost" && blogArticle) {
      breadcrumbItems.push({
        name: t("nav.blog"),
        url: `${BASE_URL}${LOCALE_URL_PREFIX[locale]}/blogg/`,
      });
      breadcrumbItems.push({
        name: t(blogArticle.titleKey),
        url: finalCanonical,
      });
    }

    if (breadcrumbItems.length > 1) {
      const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems.map((item, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": item.name,
          "item": item.url,
        })),
      };
      const bcScript = document.createElement("script");
      bcScript.id = "breadcrumb-jsonld";
      bcScript.type = "application/ld+json";
      bcScript.textContent = JSON.stringify(breadcrumbJsonLd);
      document.head.appendChild(bcScript);
    }

    // --- JSON-LD: BlogPosting (blog post pages only) ---
    const existingBlogPost = document.getElementById("blogpost-jsonld");
    if (existingBlogPost) existingBlogPost.remove();

    if (pageKey === "blogpost" && blogArticle) {
      const blogPostJsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": t(blogArticle.titleKey),
        "description": t(blogArticle.metaDescKey),
        "image": `${BASE_URL}${blogArticle.image}`,
        "datePublished": blogArticle.date,
        "dateModified": blogArticle.date,
        "author": {
          "@type": "Organization",
          "name": "Fuegos JL Nordic",
          "url": `${BASE_URL}/`,
        },
        "publisher": {
          "@type": "Organization",
          "name": "Fuegos JL Nordic",
          "url": `${BASE_URL}/`,
        },
        "url": finalCanonical,
        "mainEntityOfPage": finalCanonical,
        "inLanguage": LOCALE_TO_HTML_LANG[locale],
      };
      const bpScript = document.createElement("script");
      bpScript.id = "blogpost-jsonld";
      bpScript.type = "application/ld+json";
      bpScript.textContent = JSON.stringify(blogPostJsonLd);
      document.head.appendChild(bpScript);
    }

    // --- JSON-LD: WebPage with speakable ---
    const existingWebPage = document.getElementById("webpage-jsonld");
    if (existingWebPage) existingWebPage.remove();

    const webPageJsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": pageKey === "contact" ? "ContactPage" : "WebPage",
      "name": title,
      "description": description,
      "url": finalCanonical,
      "inLanguage": LOCALE_TO_HTML_LANG[locale],
      "isPartOf": {
        "@type": "WebSite",
        "name": "Fuegos JL Nordic",
        "url": `${BASE_URL}/`,
      },
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", ".hero-subtitle", "meta[name='description']"],
      },
    };

    const wpScript = document.createElement("script");
    wpScript.id = "webpage-jsonld";
    wpScript.type = "application/ld+json";
    wpScript.textContent = JSON.stringify(webPageJsonLd);
    document.head.appendChild(wpScript);

    return () => {
      document.getElementById("product-jsonld")?.remove();
      document.getElementById("faq-jsonld")?.remove();
      document.getElementById("breadcrumb-jsonld")?.remove();
      document.getElementById("webpage-jsonld")?.remove();
      document.getElementById("blogpost-jsonld")?.remove();
      document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
      document.querySelectorAll('meta[property="og:locale:alternate"]').forEach(el => el.remove());
    };
  }, [locale, t, pathname]);

  return null;
};

export default SEOHead;
